import React, { useMemo } from "react";
import { GroupedVirtuoso } from "react-virtuoso";
import { Text } from "@imminently/react/ui/core/text";
import { useComplaintCodes } from "./data";
import { useComplaintCodeContext } from "./ComplaintCodeContext";
import { cn } from "@/lib/utils";
import { flattenComplaintCodeTree, matchComplaintNode, type ComplaintNode } from "./util";

interface GroupLabelProps {
  total?: number;
  children: React.ReactNode;
}

const GroupLabel: React.FC<GroupLabelProps> = ({ total, children }) => {
  return (
    <div className="flex justify-between bg-white px-4 py-2 sticky top-0 z-10">
      <span className="font-semibold text-sm text-gray-700">{children}</span>
      {
        total !== undefined ?
          <span className="flex items-center font-normal text-xs justify-center w-6 h-6 rounded-full border border-blue-100">
            {total}
          </span> : null
      }
    </div>
  );
};

interface ComplaintCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The complaint code value/id */
  value: string;
  /** Whether this card is selected */
  selected?: boolean;
  className?: string;
}

export const ComplaintCodeCard: React.FC<ComplaintCardProps> = ({ value, selected, className, ...props }) => {
  const complaintData = useComplaintCodes();
  const node = useMemo(() => {
    const flat = flattenComplaintCodeTree(complaintData);
    return flat.find(n => n.id === value);
  }, [value, complaintData]);

  if (!node) {
    return (
      <div
        className={cn(
          "px-4 py-2 rounded-lg bg-white border border-transparent",
          className
        )}
        {...props}
      >
        <span className="text-sm text-gray-500">Code not found: {value}</span>
      </div>
    );
  }

  const pathParts = node.path;

  return (
    <div
      data-active={selected}
      className={cn(
        "px-4 py-2 rounded-lg bg-white border border-transparent",
        className,
        {
          'bg-yellow-50': selected,
          'border-yellow-400': selected,
        },
      )}
      {...props}
    >
      <div className="flex flex-row">
        <span className="text-sm font-medium text-gray-900">
          {node.name}
          {pathParts.length > 1 && (
            <span className="text-xs text-gray-500 ml-2 mt-1">
              {pathParts.slice(0, -1).join(" → ")}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

interface GroupItemProps {
  node: ComplaintNode;
  onClick?: (node: ComplaintNode) => void;
}

const GroupItem: React.FC<GroupItemProps> = ({ node, onClick }) => {
  const { selected } = useComplaintCodeContext();
  const isSelected = selected?.id === node.id;
  const handleClick = () => {
    onClick?.(node);
  }
  return <ComplaintCodeCard value={node.id} onClick={handleClick} selected={isSelected} className="cursor-pointer hover:bg-blue-50 transition-colors duration-150" />;
};

export const ComplaintCodeList = () => {
  const { filter, setSelected } = useComplaintCodeContext();
  const complaintData = useComplaintCodes();

  // given data, make an alphabetically sorted list and group by first letter
  const flat = flattenComplaintCodeTree(complaintData);
  const sortedData = [...flat].sort((a, b) => a.name.localeCompare(b.name));

  const processedData = useMemo(() => {
    if (filter) {
      const filteredData = sortedData.filter(item => matchComplaintNode(item.name, item.path.join("/"), filter));
      return {
        isFiltered: true,
        items: filteredData,
        groupCounts: filteredData.length > 0 ? [filteredData.length] : [],
        groupHeaders: filteredData.length > 0 ? ["Results"] : []
      };
    }

    const groupedData = sortedData.reduce((acc, item) => {
      const firstChar = item.name.charAt(0).toUpperCase();
      // Check if first character is a letter or number
      const isAlphanumeric = /[a-zA-Z0-9]/.test(firstChar);
      const groupKey = isAlphanumeric ? firstChar : '*'; // Use * to represent special symbol (e.g. '<' or '>')
      
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(item);
      return acc;
    }, {} as Record<string, ComplaintNode[]>);

    const groupHeaders = Object.keys(groupedData).sort();
    const groupCounts = groupHeaders.map(letter => groupedData[letter].length);
    const items = groupHeaders.flatMap(letter => groupedData[letter]);

    return {
      isFiltered: false,
      items,
      groupCounts,
      groupHeaders,
      groupedData
    };
  }, [sortedData, filter]);

  const handleItemClick = (node: ComplaintNode) => {
    setSelected(node);
  };

  const renderGroupHeader = (groupIndex: number) => (
    <GroupLabel total={processedData.groupCounts[groupIndex]}>
      {processedData.groupHeaders[groupIndex]}
    </GroupLabel>
  );

  const renderItem = (itemIndex: number) => (
    <GroupItem
      key={processedData.items[itemIndex].id}
      node={processedData.items[itemIndex]}
      onClick={handleItemClick}
    />
  );

  return (
    <div className="h-[600px] flex flex-col">
      {processedData.isFiltered ? (
        <>
          <div className="px-4 py-2 bg-white flex-shrink-0">
            <Text variant="h6">Related ({processedData.items.length})</Text>
          </div>
          {processedData.items.length === 0 ? (
            <div className="p-4 text-sm text-gray-500">No results found.</div>
          ) : (
            <div className="flex-1">
              <GroupedVirtuoso
                groupCounts={processedData.groupCounts}
                groupContent={() => null}
                itemContent={(index) => renderItem(index)}
                style={{ height: "100%" }}
              />
            </div>
          )}
        </>
      ) : (
        <div className="flex-1">
          {processedData.groupCounts.length > 0 && processedData.items.length > 0 ? (
            <GroupedVirtuoso
              groupCounts={processedData.groupCounts}
              groupContent={renderGroupHeader}
              itemContent={(index) => renderItem(index)}
              style={{ height: "100%" }}
            />
          ) : (
            <div className="p-4 text-sm text-gray-500">No data available.</div>
          )}
        </div>
      )}
    </div>
  );
};
